import type { TFunction } from "i18next";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function countLabel(
  t: TFunction,
  key: string,
  count: number,
  one: string,
  other: string,
): string {
  return t(key, {
    defaultValue_one: one,
    defaultValue_other: other,
    count,
  });
}

/** Day, hour, and minute labels. A span of a day or more keeps leftover minutes. Sub-minute spans stay one phrase. */
export function formatStatusDuration(durationMs: number, t: TFunction): string {
  if (durationMs < MINUTE_MS) {
    return t("orderStatusTime.duration.underMinute", "Under a minute");
  }

  const totalMinutes = Math.floor(durationMs / MINUTE_MS);
  if (durationMs < HOUR_MS) {
    return countLabel(
      t,
      "orderStatusTime.duration.minutes",
      totalMinutes,
      "{{count}} minute",
      "{{count}} minutes",
    );
  }

  const totalHours = Math.floor(durationMs / HOUR_MS);
  if (durationMs < DAY_MS) {
    const minutes = totalMinutes % 60;
    const hours = countLabel(
      t,
      "orderStatusTime.duration.hours",
      totalHours,
      "{{count}} hour",
      "{{count}} hours",
    );
    if (minutes === 0) return hours;
    const minuteLabel = countLabel(
      t,
      "orderStatusTime.duration.minutes",
      minutes,
      "{{count}} minute",
      "{{count}} minutes",
    );
    return t("orderStatusTime.duration.hoursMinutes", {
      defaultValue: "{{hours}} {{minutes}}",
      hours,
      minutes: minuteLabel,
    });
  }

  const days = Math.floor(durationMs / DAY_MS);
  const hoursRemainder = totalHours % 24;
  const minutesRemainder = totalMinutes % 60;
  const dayLabel = countLabel(
    t,
    "orderStatusTime.duration.days",
    days,
    "{{count}} day",
    "{{count}} days",
  );
  const hourLabel =
    hoursRemainder === 0
      ? null
      : countLabel(
          t,
          "orderStatusTime.duration.hours",
          hoursRemainder,
          "{{count}} hour",
          "{{count}} hours",
        );
  const minuteLabel =
    minutesRemainder === 0
      ? null
      : countLabel(
          t,
          "orderStatusTime.duration.minutes",
          minutesRemainder,
          "{{count}} minute",
          "{{count}} minutes",
        );
  if (hourLabel && minuteLabel) {
    return t("orderStatusTime.duration.daysHoursMinutes", {
      defaultValue: "{{days}} {{hours}} {{minutes}}",
      days: dayLabel,
      hours: hourLabel,
      minutes: minuteLabel,
    });
  }
  if (hourLabel) {
    return t("orderStatusTime.duration.daysHours", {
      defaultValue: "{{days}} {{hours}}",
      days: dayLabel,
      hours: hourLabel,
    });
  }
  if (minuteLabel) {
    return t("orderStatusTime.duration.daysMinutes", {
      defaultValue: "{{days}} {{minutes}}",
      days: dayLabel,
      minutes: minuteLabel,
    });
  }
  return dayLabel;
}
